import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, PageHeader, Spinner, Badge } from '../components/ui';

interface Contact { id: string; firstName: string; lastName?: string; createdAt: string; }
interface Booking { id: string; startsAt: string; status: string; contactId: string; serviceId: string; }
interface Invoice { id: string; totalCents: number; status: string; dueDate?: string; }
interface Conversation { id: string; unreadCount: number; lastMessageAt: string; }

export function DashboardPage() {
  const tenant = useAuth((s) => s.tenant);

  const contactsQ = useQuery({
    queryKey: ['contacts', 'recent'],
    queryFn: () => api.get<{ data: Contact[]; meta: { total: number } }>('/contacts', { limit: '5', sort: 'createdAt', order: 'desc' }),
  });

  const bookingsQ = useQuery({
    queryKey: ['bookings', 'upcoming'],
    queryFn: () => api.get<{ data: Booking[] }>('/bookings', { limit: '5' }).catch(() => ({ data: [] })),
  });

  const invoicesQ = useQuery({
    queryKey: ['invoices', 'outstanding'],
    queryFn: () => api.get<{ data: Invoice[]; meta: { total: number } }>('/invoices', { limit: '100' }).catch(() => ({ data: [], meta: { total: 0 } })),
  });

  const inboxQ = useQuery({
    queryKey: ['inbox', 'unread'],
    queryFn: () => api.get<{ data: Conversation[] }>('/inbox/conversations', { limit: '5' }).catch(() => ({ data: [] })),
  });

  const todayBookings = (bookingsQ.data?.data ?? []).filter((b) => {
    const day = new Date(b.startsAt).toDateString();
    return day === new Date().toDateString() && b.status !== 'cancelled';
  });

  const outstandingInvoices = (invoicesQ.data?.data ?? []).filter((i) => i.status !== 'paid' && i.status !== 'void');
  const totalDueCents = outstandingInvoices.reduce((sum, i) => sum + i.totalCents, 0);
  const unreadMessages = (inboxQ.data?.data ?? []).reduce((sum, c) => sum + c.unreadCount, 0);

  return (
    <div>
      <PageHeader
        title={`Hi, ${tenant?.name ?? 'there'}`}
        subtitle="Here's what's on today"
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
        <StatCard
          label="Today's bookings"
          value={todayBookings.length}
          hint={todayBookings.length === 0 ? 'No bookings today' : `${todayBookings.length} scheduled`}
          loading={bookingsQ.isLoading}
        />
        <StatCard
          label="Outstanding invoices"
          value={outstandingInvoices.length}
          hint={formatCents(totalDueCents)}
          loading={invoicesQ.isLoading}
        />
        <StatCard
          label="Unread messages"
          value={unreadMessages}
          hint={unreadMessages === 0 ? 'Inbox zero' : 'Take a look'}
          loading={inboxQ.isLoading}
        />
        <StatCard
          label="Contacts"
          value={contactsQ.data?.meta.total ?? 0}
          hint="Total in CRM"
          loading={contactsQ.isLoading}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
        <Card>
          <h3 style={sectionHeaderStyle}>Today's schedule</h3>
          {bookingsQ.isLoading ? <Spinner /> : todayBookings.length === 0 ? (
            <p style={mutedStyle}>Nothing scheduled. Go enjoy the day.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {todayBookings.map((b) => (
                <li key={b.id} style={rowStyle}>
                  <span>{new Date(b.startsAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                  <Badge color={b.status === 'confirmed' ? 'green' : 'blue'}>{b.status}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <h3 style={sectionHeaderStyle}>Recent contacts</h3>
          {contactsQ.isLoading ? <Spinner /> : (contactsQ.data?.data ?? []).length === 0 ? (
            <p style={mutedStyle}>No contacts yet. Add your first one from the Contacts tab.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {contactsQ.data!.data.map((c) => (
                <li key={c.id} style={rowStyle}>
                  <span>{c.firstName} {c.lastName ?? ''}</span>
                  <span style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-xs)' }}>
                    {new Date(c.createdAt).toLocaleDateString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function StatCard({ label, value, hint, loading }: { label: string; value: number | string; hint?: string; loading?: boolean }) {
  return (
    <Card>
      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 'var(--text-4xl)', fontWeight: 700, color: 'var(--color-text-primary)', marginTop: 'var(--space-1)' }}>
        {loading ? '—' : value}
      </div>
      {hint && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-1)' }}>{hint}</div>}
    </Card>
  );
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: 'var(--text-base)',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  margin: '0 0 var(--space-3)',
};

const mutedStyle: React.CSSProperties = {
  color: 'var(--color-text-tertiary)',
  fontSize: 'var(--text-sm)',
  margin: 0,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: 'var(--space-2) 0',
  borderBottom: '1px solid var(--color-border-primary)',
  fontSize: 'var(--text-sm)',
};
